import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { mobileApi } from '@/lib/mobileApi';
import { useClienteAuthStore } from '@/stores/clienteAuthStore';
import type { ClienteMobileUser } from '@/types/clienteMobile';

type PreviewExchangeResult = {
  user: ClienteMobileUser;
  accessToken: string;
  refreshToken: string;
  previewMode?: boolean;
};

const PREVIEW_CACHE_PREFIX = 'zehmotoca-preview-exchange:';
const inflightExchanges = new Map<string, Promise<PreviewExchangeResult>>();

function unwrapData<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in (raw as object)) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function cacheKey(code: string): string {
  return `${PREVIEW_CACHE_PREFIX}${code}`;
}

function readCachedExchange(code: string): PreviewExchangeResult | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(code));
    if (!raw) return null;
    return JSON.parse(raw) as PreviewExchangeResult;
  } catch {
    sessionStorage.removeItem(cacheKey(code));
    return null;
  }
}

function writeCachedExchange(code: string, payload: PreviewExchangeResult): void {
  sessionStorage.setItem(cacheKey(code), JSON.stringify(payload));
}

/** Evita consumir o código two-time (React StrictMode dispara o effect 2x). */
function exchangePreviewCodeOnce(code: string): Promise<PreviewExchangeResult> {
  const cached = readCachedExchange(code);
  if (cached) return Promise.resolve(cached);

  const inflight = inflightExchanges.get(code);
  if (inflight) return inflight;

  const promise = mobileApi
    .post('/auth/preview-exchange', { code })
    .then((res) => {
      const payload = unwrapData<PreviewExchangeResult>(res.data);
      writeCachedExchange(code, payload);
      return payload;
    })
    .finally(() => {
      inflightExchanges.delete(code);
    });

  inflightExchanges.set(code, promise);
  return promise;
}

function getApiErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  if (data?.message) return data.message;
  return 'Não foi possível iniciar a visualização. O código pode ter expirado.';
}

export function ClientePreviewPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code')?.trim() ?? '';
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!code) {
      setError('Código de preview ausente ou inválido.');
      return;
    }

    let cancelled = false;

    useClienteAuthStore.getState().logout();

    exchangePreviewCodeOnce(code)
      .then((payload) => {
        if (cancelled) return;
        useClienteAuthStore.getState().setPreviewSession(
          payload.user,
          payload.accessToken,
          payload.refreshToken
        );
        setDone(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (done) {
    return <Navigate to="/cliente/dashboard" replace />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#FAFAFA] px-4 text-center">
        <p className="text-destructive font-medium">{error}</p>
        <p className="text-sm text-muted-foreground">Feche esta janela e tente novamente pelo painel admin.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#FAFAFA]">
      <div className="h-10 w-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">Carregando visualização do app...</p>
    </div>
  );
}

/** Limpa cache de exchange após encerrar preview (modo suporte). */
export function clearPreviewExchangeCache(code?: string): void {
  if (code) {
    sessionStorage.removeItem(cacheKey(code));
    inflightExchanges.delete(code);
    return;
  }
  for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(PREVIEW_CACHE_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  }
  inflightExchanges.clear();
}
